/**
 * Terrain relief — a rolling ground surface for a world the original drew flat.
 *
 * ## Status: a documented port departure, not a reconstruction
 *
 * world-terrain.md §5 is VERIFIED and is not in dispute. Red Baron's ground
 * plane has **no per-position height**: teleporting the player across ~90 km of
 * Verdun leaves the altitude field unchanged, `.WLD` has no Z field, and
 * nothing in the 319-file inventory is grid-shaped. All of the game's relief is
 * ten placed conical hills (gi-format.md §9).
 *
 * This module adds relief anyway, and the honest framing is that **flat was the
 * original's limit, not its intent**. The evidence for "intent" is the hills
 * themselves: Dynamix built a per-object elevation carrier (`.GI`), gave three
 * landforms 727-1 905 m of height (london-sea-placements.md §9.3), wrote a
 * `giFindGround` that clamps an
 * aeroplane's Z onto a *slope*, and then placed exactly ten of them because a
 * 1990 machine could not afford the eleventh. The port has the budget.
 *
 * ## Why it is real, and not a cosmetic toggle
 *
 * The port's `GroundQuery` is physics: landings, crashes, AI ground checks,
 * lockstep duel peers and recorded `.VCR` tapes all resolve against it. A
 * *toggleable* ground would desynchronise a duel between two players who set it
 * differently and would make a tape recorded flat unreplayable over hills.
 *
 * So relief is **not** behind the ENHANCED TERRAIN switch. It is derived
 * deterministically from the region — a pure function of the region's own baked
 * content plus its name as a seed — so every peer, every replay and every
 * census computes the identical surface by construction, with no state to
 * serialise and nothing for a setting to disagree about. ENHANCED TERRAIN gates
 * only decoration drawn *on* this surface.
 *
 * (The recommended production move is to precompute this descriptor in the bake
 * stage and ship it in `data/baked/world/<REGION>.json`, which is the house
 * pattern and removes the per-load derivation cost. The function is the same
 * either way, which is what keeps the two byte-identical; see
 * `terrain-relief-source.ts`.)
 *
 * ## Where the shape comes from
 *
 * Not from taste, as far as the data allows. The carve is driven by the
 * region's own **hydrology**: the river network is real shipped geometry
 * (world-terrain.md §7.3, `BARON.PAL` index 4), and a river is the bottom of a
 * valley. Rivers therefore sit low and the land rises away from them, which is
 * free realism out of data already on disk.
 *
 * Everything between the watercourses is a smooth deterministic value noise,
 * and **that part is invented**. The corpus contains no terrain-shape data of
 * any kind beyond the ten hills, so there is nothing else to derive a general
 * roll from. Following enhanced-models.md §4.3, the added structure is listed
 * rather than blended in: the noise, its amplitude, and its wavelength.
 *
 * What keeps it disciplined is that it never *contradicts* the data:
 *
 * - **The ten hills keep their exact measured geometry and their own `.GI`
 *   collision surface.** Relief is forced to the datum across each hill's
 *   footprint, so a hill still rises from 0 exactly as authored.
 * - **Aerodromes are graded flat**, with an apron and an approach ramp. That is
 *   authentic practice rather than a dodge — a 1917 landing ground *was* a
 *   graded field — and it is what keeps a landing honest.
 * - **Amplitude is Champagne, not the Alps.** The front-line country really is
 *   gentle; a few tens of metres of roll over a couple of kilometres.
 */

/** One shipped watercourse segment, world metres, XZ. */
export interface RiverSegment {
  readonly x0: number;
  readonly z0: number;
  readonly x1: number;
  readonly z1: number;
}

/**
 * Ground that must stay at the datum: an aerodrome apron, or the footprint of
 * one of the ten real hills. Flat out to `radiusM`, then ramped back into the
 * relief over `radiusM` again — so an approach crosses no step.
 */
export interface FlatZone {
  readonly x: number;
  readonly z: number;
  readonly radiusM: number;
}

export interface ReliefOptions {
  /** Region name, hashed into the noise so two regions never share a landscape. */
  readonly seed: string;
  /** Peak height either side of the datum, metres. */
  readonly amplitudeM: number;
  /** Wavelength of the coarsest roll, metres. */
  readonly wavelengthM: number;
  /** How far below the surrounding land a watercourse sits, metres. */
  readonly riverDepthM: number;
  /** Half-width of a river valley, metres. */
  readonly riverWidthM: number;
  readonly flatZones: readonly FlatZone[];
  readonly riverSegments: readonly RiverSegment[];
}

/**
 * Tunables. Every one of these is a choice, not a measurement — the honest
 * ledger enhanced-models.md §4.3 asks for.
 *
 * `amplitudeM` and `wavelengthM` together set the steepest slope the shade walk
 * ever sees: 42 m over a 2 300 m roll is about 4° on the coarsest octave, and
 * `fbm`'s finer octaves take the local maximum to roughly 11°. That matters
 * because the palette walk quantises `cos θ` into 32 steps — under about 6° of
 * local slope the whole landscape lands on one palette entry and the relief is
 * invisible, which is the failure mode this pair is tuned against.
 */
export const DEFAULT_RELIEF = {
  amplitudeM: 42,
  wavelengthM: 2300,
  riverDepthM: 26,
  riverWidthM: 750,
} as const;

/** Graded apron around an aerodrome, metres. Roughly a circuit, so an approach never crosses a carve. */
export const AERODROME_FLAT_M = 2200;

/** FNV-1a over the seed string — deterministic across machines and engines. */
function seedHash(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Integer hash → [0,1). All-integer until the final divide, so it is bit-identical everywhere. */
function hash01(ix: number, iz: number, salt: number): number {
  let h = Math.imul(ix, 0x27d4eb2d) ^ Math.imul(iz, 0x165667b1) ^ salt;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

function smoothstep(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}

function valueNoise(x: number, z: number, salt: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = smoothstep(x - ix);
  const fz = smoothstep(z - iz);
  const a = hash01(ix, iz, salt);
  const b = hash01(ix + 1, iz, salt);
  const c = hash01(ix, iz + 1, salt);
  const d = hash01(ix + 1, iz + 1, salt);
  return (a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz;
}

/**
 * Three octaves in [0,1]. Three rather than one because the shade walk reads
 * the *gradient*: a single 2.3 km wave at 42 m never exceeds ~4°, which is
 * under one palette step, and the surface would be shaded a flat single colour.
 * The finer octaves are what make it read as land rather than as a swell.
 */
function fbm(x: number, z: number, wavelengthM: number, salt: number): number {
  const f = 1 / wavelengthM;
  return (
    valueNoise(x * f, z * f, salt) * 0.6 +
    valueNoise(x * f * 2.3 + 31.7, z * f * 2.3 - 17.3, salt) * 0.27 +
    valueNoise(x * f * 5.1 - 11.1, z * f * 5.1 + 43.9, salt) * 0.13
  );
}

function pointSegmentDistanceSq(px: number, pz: number, s: RiverSegment): number {
  const dx = s.x1 - s.x0;
  const dz = s.z1 - s.z0;
  const lenSq = dx * dx + dz * dz;
  const t = lenSq > 0 ? Math.min(1, Math.max(0, ((px - s.x0) * dx + (pz - s.z0) * dz) / lenSq)) : 0;
  const cx = s.x0 + t * dx;
  const cz = s.z0 + t * dz;
  return (px - cx) * (px - cx) + (pz - cz) * (pz - cz);
}

/** Uniform bucket grid: a region carries thousands of river segments and the mesh takes ~10^5 samples. */
class SegmentIndex {
  private readonly cell: number;
  private readonly buckets = new Map<number, RiverSegment[]>();

  constructor(segments: readonly RiverSegment[], cellM: number) {
    this.cell = cellM;
    for (const s of segments) {
      const x0 = Math.floor(Math.min(s.x0, s.x1) / cellM);
      const x1 = Math.floor(Math.max(s.x0, s.x1) / cellM);
      const z0 = Math.floor(Math.min(s.z0, s.z1) / cellM);
      const z1 = Math.floor(Math.max(s.z0, s.z1) / cellM);
      for (let cx = x0; cx <= x1; cx++) {
        for (let cz = z0; cz <= z1; cz++) {
          const key = cx * 73856093 + cz;
          const list = this.buckets.get(key);
          if (list) list.push(s);
          else this.buckets.set(key, [s]);
        }
      }
    }
  }

  nearest(px: number, pz: number): number {
    const cx = Math.floor(px / this.cell);
    const cz = Math.floor(pz / this.cell);
    let best = Infinity;
    for (let ix = cx - 1; ix <= cx + 1; ix++) {
      for (let iz = cz - 1; iz <= cz + 1; iz++) {
        const list = this.buckets.get(ix * 73856093 + iz);
        if (!list) continue;
        for (const s of list) {
          const d = pointSegmentDistanceSq(px, pz, s);
          if (d < best) best = d;
        }
      }
    }
    return best === Infinity ? Infinity : Math.sqrt(best);
  }
}

export interface ReliefField {
  /** Ground height in metres above the datum at a world XZ. */
  readonly heightAt: (x: number, z: number) => number;
  readonly options: ReliefOptions;
}

/**
 * Builds the height field. Pure and deterministic: identical options give
 * identical numbers on any machine — integer hashing plus IEEE double
 * arithmetic, no iteration-order dependence, no `Math.random`, no time.
 */
export function createReliefField(options: ReliefOptions): ReliefField {
  const salt = seedHash(options.seed) | 0;
  // One cell per valley half-width means `nearest` only ever scans the 3x3
  // block around the sample, which is what keeps a 10^5-sample mesh affordable.
  const index = new SegmentIndex(options.riverSegments, Math.max(200, options.riverWidthM));

  const heightAt = (x: number, z: number): number => {
    // Data guard: aerodrome aprons and the ten real hills sit at the datum, so
    // a hill still rises from 0 exactly as authored and a landing ground is
    // graded flat. Ramped over the zone's own radius again, never a step.
    let graded = 1;
    for (const zone of options.flatZones) {
      const d = Math.hypot(x - zone.x, z - zone.z);
      if (d >= zone.radiusM * 2) continue;
      graded = Math.min(graded, smoothstep((d - zone.radiusM) / zone.radiusM));
      if (graded <= 0) return 0;
    }

    const roll = (fbm(x, z, options.wavelengthM, salt) * 2 - 1) * options.amplitudeM;
    const dRiver = index.nearest(x, z);
    // Gaussian trough centred on the watercourse: full depth on the water,
    // back to the open roll about two half-widths away.
    const valley = dRiver === Infinity ? 0 : Math.exp(-((dRiver / options.riverWidthM) ** 2)) * options.riverDepthM;

    return (roll - valley) * graded;
  };

  return { heightAt, options };
}

/**
 * A flat field, for callers with no region — the practice range, the two
 * coastal maps, unit tests.
 *
 * Its amplitude and river depth are **zero**, and that is load-bearing rather
 * than cosmetic: `reliefIsFlat` is what stops the rest of the port doing
 * expensive work for a surface that is not there. Left at the defaults, London
 * would subdivide its 378 x 245 km sea plate to a 320 m edge — 1.17 million
 * triangles — and build a 295 000-triangle mesh, all of it dead flat.
 */
export const FLAT_RELIEF: ReliefField = {
  heightAt: () => 0,
  options: { seed: "", ...DEFAULT_RELIEF, amplitudeM: 0, riverDepthM: 0, flatZones: [], riverSegments: [] },
};

/** Whether this field has any relief at all — see `FLAT_RELIEF`. */
export function reliefIsFlat(relief: ReliefField): boolean {
  return relief.options.amplitudeM <= 0 && relief.options.riverDepthM <= 0;
}

/**
 * The bound relief is contractually held to. Not a clamp — a check, so a test
 * can walk a real region and fail loudly if a tuning change makes the ground
 * steeper than the flight model and the aerodrome grading were verified against.
 */
export function reliefWithinBounds(height: number, options: ReliefOptions): boolean {
  const limit = options.amplitudeM + options.riverDepthM;
  return Number.isFinite(height) && Math.abs(height) <= limit + 1e-6;
}
